import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Activity, HelpCircle, Eye, Grid, Hash, RefreshCw, Lock, Fingerprint, Shield, Layers, Info, ChevronDown, ChevronUp, Zap, Maximize, Minimize, Copy } from 'lucide-react';

const HashVisualizer = ({ hashResult, algorithm = "MD5" }) => {
  const [vizMode, setVizMode] = useState('spectrum');
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // Convert hash to numerical values
  const getHashValues = () => {
    if (!hashResult) return [];
    return Array.from(hashResult).map(char => parseInt(char, 16) || 0);
  };
  
  const copyToClipboard = () => {
    if (hashResult) {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(hashResult)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          })
          .catch(err => {
            console.error("Failed to copy to clipboard:", err);
            // Optionally provide user feedback about the failure
          });
      } else {
        // Fallback for browsers that don't support navigator.clipboard
        fallbackCopyToClipboard(hashResult);
        setCopied(true); // Indicate "copied" even if using fallback
        setTimeout(() => setCopied(false), 2000);
        console.warn("navigator.clipboard not supported, using fallback.");
        // Optionally inform the user that a less reliable method was used
      }
    }
  };
  
  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
  
    // Avoid showing the textarea
    textArea.style.position = "fixed";
    textArea.style.top = 0;
    textArea.style.left = 0;
    textArea.style.width = "2em";
    textArea.style.height = "2em";
    textArea.style.padding = 0;
    textArea.style.border = "none";
    textArea.style.outline = "none";
    textArea.style.boxShadow = "none";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
      // Optionally provide user feedback about the failure
    }
    document.body.removeChild(textArea);
  };
  
  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };
  
  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  
  // Canvas-based spectrum visualization
  useEffect(() => {
    if (vizMode !== 'spectrum' || !canvasRef.current || !hashResult) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const hashValues = getHashValues();
    
    // Set canvas dimensions
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw spectrum visualization
    const barWidth = canvas.width / hashValues.length;
    const maxValue = 15; // Max hex value
    
    // Create gradient
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    gradient.addColorStop(0, '#3b82f6');   // blue-500
    gradient.addColorStop(0.5, '#6366f1'); // indigo-500
    gradient.addColorStop(1, '#8b5cf6');   // violet-500
    
    // Animation
    let currentStep = 0;
    const totalSteps = 30;
    
    const animate = () => {
      if (currentStep >= totalSteps) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw bars with animation
      hashValues.forEach((value, i) => {
        const normalizedHeight = (value / maxValue) * canvas.height * 0.8;
        const animatedHeight = (normalizedHeight * (currentStep / totalSteps));
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(
          i * barWidth, 
          canvas.height - animatedHeight, 
          barWidth - 1, 
          animatedHeight, 
          [2, 2, 0, 0]
        );
        ctx.fill();
        
        // Add glow effect
        ctx.shadowColor = '#3b82f6';
        ctx.shadowBlur = 5;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      });
      
      currentStep++;
      requestAnimationFrame(animate);
    };
    
    animate();
  }, [hashResult, vizMode]);
  
  // Hash security strength indicator
  const getSecurityStrength = () => {
    if (!hashResult) return { text: 'No hash', color: 'text-gray-500' };
    
    const length = hashResult.length;
    
    if (length >= 64) return { text: 'Very Strong', color: 'text-green-500' };
    if (length >= 40) return { text: 'Strong', color: 'text-emerald-500' };
    if (length >= 32) return { text: 'Good', color: 'text-blue-500' };
    return { text: 'Basic', color: 'text-yellow-500' };
  };
  
  const securityStrength = getSecurityStrength();
  
  // Hash algorithm details
  const getAlgorithmDetails = () => {
    const algorithms = {
        'md5': {
          bits: 128,
          example: '5d41402abc4b2a76b9719d911017c592',
          useCase: 'File integrity checking (though no longer secure for cryptographic purposes)',
          weakness: 'Vulnerable to collision attacks',
          color: 'from-yellow-500 to-orange-600'
        },
        'sha1': {
          bits: 160,
          example: 'aaf4c61ddcc5e8a2dabede0f3b482cd9aea9434d',
          useCase: 'Git version control, legacy systems',
          weakness: 'Collision attacks demonstrated in practice',
          color: 'from-orange-500 to-red-600'
        },
        'sha224': {
          bits: 224,
          example: 'a5b215b6f4d80dd3c8467b03baee8f68f294c64d4ba9180b9e5d97c8',
          useCase: 'Used in applications requiring moderate security',
          weakness: 'Has known vulnerabilities',
          color: 'from-green-300 to-green-500'
        },
        'sha256': {
          bits: 256,
          example: '9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08',
          useCase: 'Digital signatures, file integrity, password storage',
          weakness: 'No practical weaknesses known',
          color: 'from-emerald-500 to-green-600'
        },
        'sha384': {
          bits: 384,
          example: '8e9b0c2c71f1d3d283501428b0f61db46e79ff3f56216e9806c3f191a82a7434d75aac173d176ed1ab3dcadfe67811e58f4e6',
          useCase: 'Secure communications',
          weakness: 'Very high security but slower than SHA-256',
          color: 'from-teal-500 to-cyan-600'
        },
        'sha512': {
          bits: 512,
          example: 'cf83e1357eefb8bdf1542850d66d8007d620e4050b5715dc83f4a921d36ce9ce3b47a4d6aa9dff7c65be91b45d0c0f7e...',
          useCase: 'High-security applications, password storage',
          weakness: 'No practical weaknesses known',
          color: 'from-teal-500 to-emerald-600'
        },
        'blake2b': {
          bits: 512,
          example: '0e5751c026e543b2e8ab2eb06099daa1d1e5df47778f7787faab45cdf12fe3a8',
          useCase: 'High-performance cryptographic verification',
          weakness: 'No practical weaknesses known',
          color: 'from-blue-500 to-indigo-600'
        },
        'blake2s': {
          bits: 256,
          example: '2e86b7f2b9511985744759ff48c94425f009fd3b397b231b755cf9daba45a755',
          useCase: 'High-performance cryptographic verification',
          weakness: 'No practical weaknesses known',
          color: 'from-blue-400 to-blue-500'
        },
        'sha3-224': {
          bits: 224,
          example: '0e5c8e3e3d5de5e2ee068ce32b7b1d0c8a542633b7878095ec03cc37d85d3e06',
          useCase: 'Secure hashing designed for efficiency',
          weakness: 'None known',
          color: 'from-purple-500 to-purple-600'
        },
        'sha3-256': {
          bits: 256,
          example: 'a4d15e49b04c07aad85300bbcdc894d5fc823a68bf9f1b85eebf3a43c278ffd7',
          useCase: 'Secure applications requiring better security',
          weakness: 'None known',
          color: 'from-purple-500 to-purple-600'
        },
        'sha3-384': {
          bits: 384,
          example: 'c4eae185afeb216632c0c5d3ad18b5ee4faa06e9a9cbc84873e4a7d95c7db14c7fd21e1dc7799a8cf5a8030b43ab70c3a053e',
          useCase: 'High-security applications',
          weakness: 'None known',
          color: 'from-purple-600 to-purple-700'
        },
        'sha3-512': {
          bits: 512,
          example: 'b23d1aa6797a4c35308656975bb6d67a42d48baf57659cc4bd0356b7f27d0de582d3f8e3570de568218cfd47a0055d0da54fec4a2d99e53',
          useCase: 'Ultra-secure hashing',
          weakness: 'None known',
          color: 'from-purple-700 to-purple-800'
        },
        'argon2': {
          bits: 'Variable (depends on configuration)',
          example: '10$5c9b47a4a23d09cff9fd904501e3a8e30c9aab089e3c7476411034dd05b1b5e8',
          useCase: 'Password hashing, securing sensitive data',
          weakness: 'None known, design secure configurations',
          color: 'from-black to-gray-700'
        },
        'bcrypt': {
          bits: 192, // As it is typically 192 bits in practice
          example: '$2b$12$KIX0dOZOAUNZz5b.ljGhWOxpalcpKcz0B0HA1iKe90dUb2wuyZcwS',
          useCase: 'Password hashing',
          weakness: 'Long passwords may lead to inefficient performance',
          color: 'from-black to-gray-800'
        },
        'pbkdf2_sha256': {
          bits: 256,
          example: '16$e$ALGnMUhI.5LtVZ/kMIn3qfQMT5z3ucQ',
          useCase: 'Password hashing with a high work factor',
          weakness: 'Length of salt matters, ensure sufficient iterations',
          color: 'from-gray-600 to-gray-700'
        },
        'pbkdf2_sha512': {
          bits: 512,
          example: '32$6FD664F19320CC5C59CD1ADFFC869D3C69DD733B5860C1D411A60BCC6D2ACC4',
          useCase: 'High-security password hashing',
          weakness: 'Requires careful parameter selection',
          color: 'from-gray-500 to-gray-600'
        },
      };
    
    return algorithms[algorithm] || algorithms['SHA256'];
  };
  
  const algorithmDetails = getAlgorithmDetails();
  
  // Hash pattern analysis - simulate finding patterns
  const getPatternAnalysis = () => {
    if (!hashResult) return [];
    
    const values = getHashValues();
    const patterns = [];
    
    // Calculate entropy (simplified)
    const frequencies = {};
    values.forEach(val => {
      frequencies[val] = (frequencies[val] || 0) + 1;
    });
    
    const entropy = Object.values(frequencies).reduce((acc, freq) => {
      const p = freq / values.length;
      return acc - p * Math.log2(p);
    }, 0);
    
    patterns.push({
      name: 'Entropy',
      value: entropy.toFixed(2),
      description: 'Measure of randomness',
      status: entropy > 3 ? 'good' : 'warning'
    });
    
    // Detect repeating sequences (simplified)
    let repeats = 0;
    for (let i = 0; i < values.length - 3; i++) {
      if (values[i] === values[i+2] && values[i+1] === values[i+3]) {
        repeats++;
      }
    }
    
    patterns.push({
      name: 'Repeating Patterns',
      value: repeats,
      description: 'Detected repetitions',
      status: repeats < 3 ? 'good' : 'warning'
    });
    
    // Detect bias towards certain values
    const sum = values.reduce((a, b) => a + b, 0);
    const mean = sum / values.length;
    const idealMean = 7.5; // Ideal mean for hex digits (0-15)
    const bias = Math.abs(mean - idealMean);
    
    patterns.push({
      name: 'Distribution Bias',
      value: bias.toFixed(2),
      description: 'Deviation from ideal',
      status: bias < 1 ? 'good' : bias < 2 ? 'warning' : 'alert'
    });
    
    return patterns;
  };
  
  // Render hash in different visualization modes
  const renderVisualization = () => {
    const values = getHashValues();
    
    switch (vizMode) {
      case 'spectrum':
        return (
          <div className="w-full h-32 relative">
            <canvas 
              ref={canvasRef} 
              className="w-full h-full"
            />
          </div>
        );
        
      case 'grid':
        return (
          <div className="grid grid-cols-8 md:grid-cols-16 gap-1 p-2">
            {values.map((val, i) => (
              <div 
                key={i}
                className="aspect-square rounded-sm flex items-center justify-center text-xs font-mono transition-all duration-300 hover:scale-110"
                style={{
                  backgroundColor: `rgba(59, 130, 246, ${val / 15})`,
                  color: val > 7 ? 'white' : 'rgba(255,255,255,0.7)'
                }}
              >
                {val.toString(16).toUpperCase()}
              </div>
            ))}
          </div>
        );
        
      case 'wave':
        return (
          <div className="relative h-32 w-full flex items-center justify-center">
            <div className="absolute inset-0 flex items-center">
              <svg width="100%" height="100%" viewBox="0 0 100 20">
                <defs>
                  <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="50%" stopColor="#6366f1" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
                <path
                  d={`M 0 10 ${values.map((val, i) => 
                    `L ${(i / values.length) * 100} ${10 - val / 15 * 8}`
                  ).join(' ')} L 100 10`}
                  fill="none"
                  stroke="url(#waveGradient)"
                  strokeWidth="0.5"
                  strokeLinecap="round"
                />
                {values.map((val, i) => (
                  <circle
                    key={i}
                    cx={`${(i / values.length) * 100}`}
                    cy={`${10 - val / 15 * 8}`}
                    r="0.4"
                    fill="#a5b4fc"
                  />
                ))}
              </svg>
            </div>
          </div>
        );
        
      case 'blocks':
        return (
          <div className="flex flex-wrap gap-1 p-2 justify-center">
            {Array.from({ length: Math.min(values.length, 64) }).map((_, i) => {
              // Group values by pairs for more variation
              const idx = i * 2;
              const val1 = values[idx] || 0;
              const val2 = values[idx + 1] || 0;
              const combinedValue = val1 * 16 + val2;
              const hue = (combinedValue % 360);
              
              return (
                <div 
                  key={i}
                  className="w-6 h-6 rounded-md transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-blue-500/20"
                  style={{
                    backgroundColor: `hsl(${hue}, 80%, ${30 + (combinedValue % 40)}%)`,
                    transform: `rotate(${(combinedValue % 4) * 90}deg)`
                  }}
                ></div>
              );
            })}
          </div>
        );
        
      default:
        return (
          <div className="text-center p-4 text-gray-400">
            Select a visualization mode
          </div>
        );
    }
  };
  
  if (!hashResult) {
    return (
      <div className="bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-lg">
        <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800">
          <h3 className="text-lg font-semibold flex items-center">
            <BarChart className="w-5 h-5 mr-2 text-blue-400" />
            Hash Visualization
          </h3>
        </div>
        <div className="p-8 flex flex-col items-center justify-center text-center space-y-4">
          <Hash className="w-12 h-12 text-blue-500/30" />
          <p className="text-gray-400">
            No hash data to visualize yet
          </p>
          <p className="text-sm text-gray-500">
            Generate a hash to see advanced visualization
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      ref={containerRef}
      className={`bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-lg transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''}`}
    >
      <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800 flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-blue-400" />
          Hash Visualization
        </h3>
        <div className="flex items-center space-x-2">
          <button 
            onClick={toggleFullscreen}
            className="p-1.5 rounded-md hover:bg-gray-800/50 text-gray-400 hover:text-white transition-colors"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
        </div>
      </div>
      
      {/* Hash info bar */}
      <div className="bg-black/30 px-4 py-2 flex flex-wrap items-center justify-between border-b border-gray-800/50">
        <div className="flex items-center space-x-3">
          <div className="flex items-center">
            <Lock className="w-3.5 h-3.5 text-blue-400 mr-1.5" />
            <span className="text-xs font-mono text-gray-300">{algorithm}</span>
          </div>
          <div className="flex items-center">
            <Shield className="w-3.5 h-3.5 mr-1.5" style={{ color: securityStrength.color.replace('text-', '') }} />
            <span className={`text-xs ${securityStrength.color}`}>{securityStrength.text}</span>
          </div>
          <div className="flex items-center">
            <Layers className="w-3.5 h-3.5 text-gray-400 mr-1.5" />
            <span className="text-xs text-gray-400">{hashResult.length * 4} bits</span>
          </div>
        </div>
        <div className="flex items-center">
          <button
            onClick={copyToClipboard}
            className="text-xs flex items-center text-gray-400 hover:text-white transition-colors"
          >
            {copied ? 'Copied!' : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
          </button>
        </div>
      </div>
      
      {/* Visualization mode selector */}
      <div className="flex border-b border-gray-800/50">
        {['spectrum', 'grid', 'wave', 'blocks'].map(mode => (
          <button
            key={mode}
            onClick={() => setVizMode(mode)}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${
              vizMode === mode 
                ? 'text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Main visualization area */}
      <div className="bg-black/20 border-b border-gray-800/50">
        {renderVisualization()}
      </div>
      
      {/* Hash preview */}
      <div className="bg-black/40 px-4 py-3 border-b border-gray-800/50">
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">Hash Value:</div>
          <div className="text-xs text-gray-400 hover:text-white transition-colors cursor-pointer" onClick={copyToClipboard}>
            <Copy className="w-3 h-3 inline mr-1" />
            Copy
          </div>
        </div>
        <div className="font-mono text-sm text-blue-300 mt-1 break-all">
          {hashResult.length > 40 && !isFullscreen 
            ? `${hashResult.substring(0, 40)}...`
            : hashResult}
        </div>
      </div>
      
      {/* Pattern analysis */}
      <div className="p-4 border-b border-gray-800/50">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-semibold text-gray-300 flex items-center">
            <Activity className="w-4 h-4 mr-1.5 text-indigo-400" />
            Pattern Analysis
          </h4>
          <div className="text-xs text-blue-400">Real-time</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {getPatternAnalysis().map((pattern, i) => (
            <div key={i} className="bg-black/30 rounded p-2 border border-gray-800/50">
              <div className="text-xs font-medium text-gray-400">{pattern.name}</div>
              <div className="flex items-center justify-between mt-1">
                <div className={`text-lg font-mono ${
                  pattern.status === 'good' ? 'text-green-500' : 
                  pattern.status === 'warning' ? 'text-yellow-500' : 'text-red-500'
                }`}>
                  {pattern.value}
                </div>
                <div className={`text-xs px-1.5 py-0.5 rounded ${
                  pattern.status === 'good' ? 'bg-green-900/30 text-green-400' : 
                  pattern.status === 'warning' ? 'bg-yellow-900/30 text-yellow-400' : 'bg-red-900/30 text-red-400'
                }`}>
                  {pattern.status.toUpperCase()}
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">{pattern.description}</div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Algorithm information */}
      <div className={`border-b border-gray-800/50 transition-all duration-300 ${infoExpanded ? '' : 'max-h-12 overflow-hidden'}`}>
        <button 
          onClick={() => setInfoExpanded(!infoExpanded)}
          className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-800/20 transition-colors"
        >
          <div className="flex items-center">
            <Info className="w-4 h-4 mr-2 text-blue-400" />
            <h4 className="text-sm font-medium text-gray-300">About {algorithm} Hashing</h4>
          </div>
          <div>
            {infoExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </button>
        
        <div className="px-4 pb-4 text-sm text-gray-400 space-y-4">
          <p>
            {algorithm} is a cryptographic hash function that produces a {algorithmDetails.bits}-bit hash value, 
            typically rendered as a hexadecimal number of {algorithmDetails.bits/4} digits.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-black/20 rounded-lg p-3 border border-gray-800/50">
              <div className="text-xs text-gray-500 mb-1">Common Use Case</div>
              <div className="text-sm">{algorithmDetails.useCase}</div>
            </div>
            
            <div className="bg-black/20 rounded-lg p-3 border border-gray-800/50">
              <div className="text-xs text-gray-500 mb-1">Security Status</div>
              <div className="text-sm">{algorithmDetails.weakness}</div>
            </div>
          </div>
          
          <div>
            <div className="text-xs text-gray-500 mb-1">How Hashing Works</div>
            <div className="relative w-full h-16 mt-2 bg-black/20 rounded-lg overflow-hidden">
              <div className="absolute inset-0 flex items-center">
                <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-blue-500/30 to-transparent relative">
                  <div 
                    className={`absolute top-1/2 left-0 h-4 w-4 -translate-y-1/2 rounded-full bg-gradient-to-r ${algorithmDetails.color} animate-pulse-slow`}
                    style={{
                      boxShadow: '0 0 10px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    <div className="absolute inset-0 rounded-full animate-ping-slow opacity-75 bg-blue-500"></div>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-0 left-0 w-1/3 h-full flex flex-col items-center justify-center text-xs">
                <div className="text-gray-400">Input</div>
                <div className="text-gray-300 font-mono mt-1">data</div>
              </div>
              
              <div className="absolute top-0 left-1/3 w-1/3 h-full flex flex-col items-center justify-center text-xs">
                <div className="text-blue-400">Hashing Algorithm</div>
                <div className="text-blue-300 font-mono mt-1">
                  <Zap className="inline w-3 h-3" /> {algorithm}
                </div>
              </div>
              
              <div className="absolute top-0 right-0 w-1/3 h-full flex flex-col items-center justify-center text-xs">
                <div className="text-gray-400">Output</div>
                <div className="text-gray-300 font-mono mt-1 truncate max-w-full px-2">
                  {algorithmDetails.example.substring(0, 8)}...
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-gray-500">
            Important properties of {algorithm}: (1) deterministic - same input always results in same output,
            (2) pre-image resistant - cannot feasibly reverse the hash to obtain the original input,
            (3) small changes in input cause large changes in output (avalanche effect).
          </p>
        </div>
      </div>
    </div>
  );
};

export default HashVisualizer;